import { AnimatePresence, motion } from 'motion/react';
import { useId, useEffect, useRef, useState } from 'react';

interface AccordionItem {
  title: string;
  description: string;
}

interface ProductDesktopAccordionProps {
  items: AccordionItem[];
  idPrefix?: string;
}

export const ProductDesktopAccordion = ({
  items,
  idPrefix,
}: ProductDesktopAccordionProps) => {
  const fallbackId = useId();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const baseId = idPrefix || fallbackId;
  const containerRef = useRef<HTMLDivElement>(null);

  /* Close open item on click outside */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        openIndex !== null &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpenIndex(null);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [openIndex]);

  return (
    <div ref={containerRef} className="flex flex-col w-full">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const contentId = `${baseId}-content-${index}`;
        const buttonId = `${baseId}-button-${index}`;

        return (
          <div key={`${baseId}-${index}`} className="pt-4">
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={contentId}
              id={buttonId}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className="group flex w-full items-center gap-[30px] text-left cursor-pointer"
            >
              <div className="flex-1">
                <p className="font-['Manrope','Manrope_Fallback',sans-serif] text-base leading-[1.5] tracking-[0.16px] text-black font-medium transition-colors duration-300 group-hover:text-graphite-primary">
                  {item.title}
                </p>
              </div>
              {/* Plus → Minus: vertical bar rotates 90° into horizontal */}
              <span className="relative flex size-6 items-center justify-center">
                <span className="absolute h-px w-4 bg-black transition-colors duration-300 group-hover:bg-graphite-primary" />
                <motion.span
                  className="absolute h-4 w-px bg-black transition-colors duration-300 group-hover:bg-graphite-primary"
                  animate={{
                    rotate: isOpen ? 90 : 0,
                    opacity: isOpen ? 0 : 1,
                  }}
                  transition={{
                    duration: 0.3,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                  initial={false}
                />
              </span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  id={contentId}
                  role="region"
                  aria-labelledby={buttonId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
                  }}
                  className="overflow-hidden"
                >
                  <div className="mt-2">
                    <p className="font-['Manrope','Manrope_Fallback',sans-serif] text-base leading-[1.5] tracking-[0.16px] text-black">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="mt-4 h-px w-full bg-black" />
          </div>
        );
      })}
    </div>
  );
};
